import { Suspense, lazy, useEffect, useState, type ComponentType, type ReactNode } from "react";
// Toasters are deferred — they sit idle until a toast fires, so we ship
// sonner (~10 KB) + Radix Toast (~3 KB) out of the initial bundle.
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// TooltipProvider deferred — only LanguageFlags (footer) and ComparisonTable
// (lazy /services route) render tooltips, so the entire @radix-ui/react-tooltip
// + @floating-ui chain (~10 KB) sits out of the initial bundle. We use a
// post-mount dynamic import (not React.lazy) so the app tree renders ONCE:
// children pass through directly until the chunk arrives, then we re-render
// with the real Provider wrapping them. No router/state remount.
const DeferredTooltipProvider = ({ children }: { children: ReactNode }) => {
  const [Provider, setProvider] = useState<ComponentType<{ children: ReactNode }> | null>(null);
  useEffect(() => {
    let cancelled = false;
    import("@/components/ui/tooltip").then((m) => {
      if (!cancelled) setProvider(() => m.TooltipProvider);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return Provider ? <Provider>{children}</Provider> : <>{children}</>;
};
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index"; // home loaded eagerly for fast LCP
import { CMSAuthProvider } from "@/contexts/CMSAuthContext";
import { ProtectedCMSRoute } from "@/components/cms/ProtectedCMSRoute";
import BrickBot from "@/components/Chat/BrickBot";
import { LazyMotion, domAnimation } from "framer-motion";
import { MotionRailProvider } from "@/components/motion/MotionRailProvider";
import { SoundProvider } from "@/components/motion/SoundProvider";
import ScrollRail from "@/components/motion/ScrollRail";
import MagneticCursor from "@/components/motion/MagneticCursor";
import LoadFilm from "@/components/motion/LoadFilm";

/**
 * Route-level code splitting. Every non-home page is lazy-loaded so the initial
 * JS payload stays close to home + chat + chrome only. The Suspense fallback
 * uses canvas tokens to avoid a flash of white between chunks.
 */
const NotFound = lazy(() => import("./pages/NotFound"));
const BrickLive = lazy(() => import("./pages/BrickLive"));
const AIAdvantageLab = lazy(() => import("./pages/AIAdvantageLab"));
const AIAgents = lazy(() => import("./pages/AIAgents"));
const AIEmployees = lazy(() => import("./pages/AIEmployees"));
const AIAgentDevelopment = lazy(() => import("./pages/AIAgentDevelopment"));
const AIAdvantageLabConfirmation = lazy(() => import("./pages/AIAdvantageLabConfirmation"));
const WhatWeDo = lazy(() => import("./pages/WhatWeDo"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blick = lazy(() => import("./pages/Blick"));
const Careers = lazy(() => import("./pages/Careers"));
const AnimationPreview = lazy(() => import("./pages/AnimationPreview"));
const FAQPage = lazy(() => import("@/components/FAQ/FAQPage"));
const AIDictionary = lazy(() => import("./pages/AIDictionary"));
const CMSDashboard = lazy(() => import("./pages/cms/CMSDashboard"));
const CMSBrickLab = lazy(() => import("./pages/cms/CMSBrickLab"));
const CMSTranslations = lazy(() => import("./pages/cms/CMSTranslations"));
const CMSLogin = lazy(() => import("./pages/cms/CMSLogin"));
const EUAIActOverview = lazy(() => import("./pages/EUAIActOverview"));
const EUAIActDeepDive = lazy(() => import("./pages/EUAIActDeepDive"));
const AIRiskAssessment = lazy(() => import("./pages/AIRiskAssessment"));
const AlgorithmHotel = lazy(() => import("./pages/AlgorithmHotel"));
const GuideScalingAIAdoption = lazy(() => import("./pages/GuideScalingAIAdoption"));
const FutureCustomerExperience = lazy(() => import("./pages/FutureCustomerExperience"));
const HowWeWork = lazy(() => import("./pages/HowWeWork"));
const Work = lazy(() => import("./pages/Work"));
const PrivateEquity = lazy(() => import("./pages/PrivateEquity"));
const AIAgenter = lazy(() => import("./pages/sv/AIAgenter"));
const AIArbetsflode = lazy(() => import("./pages/sv/AIArbetsflode"));
const Returfrakt = lazy(() => import("./pages/sv/Returfrakt"));
const IndustriesHub = lazy(() => import("./pages/verticals/IndustriesHub"));
const VerticalRoute = lazy(() => import("./pages/verticals/VerticalRoute"));
const SolutionRoute = lazy(() => import("./pages/solutions/SolutionRoute"));
// Kundspecifik konceptkampanj (noindex, fristående chrome).
const TristarLanding = lazy(() => import("./pages/tristar/TristarLanding"));
const TristarMetoden = lazy(() => import("./pages/tristar/TristarMetoden"));
const Bora = lazy(() => import("./pages/bora/Bora"));
const ClientOnboarding = lazy(() => import("./pages/onboarding/ClientOnboarding"));
import { useLocation } from "react-router-dom";

/**
 * Chrome (promo banner + persistent chatbot) is suppressed on standalone
 * paid landing pages that own their own header/footer. Add path prefixes
 * here — keep the list tiny; global chrome should stay on for normal pages.
 */
const STANDALONE_PATH_PREFIXES = ["/ai-arbetsflode", "/sv/returfrakt", "/tristar", "/live", "/bora", "/onboarding"];
const useIsStandaloneLP = () => {
  const { pathname } = useLocation();
  return STANDALONE_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};
const ChromeGate = ({ children }: { children: ReactNode }) => {
  const standalone = useIsStandaloneLP();
  return standalone ? null : <>{children}</>;
};

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--bg-canvas))]">
    <div className="animate-pulse text-[hsl(var(--fg-muted))]">Loading...</div>
  </div>
);

const publicRoutes = [
  { path: "", element: <Index /> },
  { path: "live", element: <BrickLive /> },

  // ─── AI Employees repositioning (Phase 1) ─────────────────────────────
  { path: "ai-employees", element: <AIEmployees /> },
  // Localised SV slug for the same page. English URL (/ai-medarbetare)
  // redirects to /ai-employees; SV URL (/sv/ai-medarbetare) is the SV
  // canonical. See src/config/sitemapRoutes.ts for hreflang wiring.
  { path: "ai-medarbetare", element: <AIEmployees /> },
  { path: "ai-agent-development", element: <AIAgentDevelopment /> },
  // Retired product/pricing URLs — Brick no longer sells packaged offers.
  { path: "get-your-first-ai-employee", element: <Navigate to="/what-we-do" replace /> },
  { path: "find-your-first-ai-employee", element: <Navigate to="/what-we-do" replace /> },
  { path: "pricing", element: <Navigate to="/what-we-do" replace /> },

  // ─── New commercial spine ─────────────────────────────────────────────
  { path: "future-customer-experience", element: <FutureCustomerExperience /> },
  { path: "how-we-work", element: <HowWeWork /> },
  { path: "work", element: <Work /> },
  { path: "private-equity", element: <PrivateEquity /> },

  // Capability hub. Old /what-we-build and /services URLs redirect here.
  { path: "what-we-do", element: <WhatWeDo /> },
  { path: "what-we-build", element: <Navigate to="/what-we-do" replace /> },
  { path: "results", element: <Navigate to="/work" replace /> },

  // Old product-shaped delivery URLs now resolve to the one client pathway.
  { path: "how-we-work/sprint", element: <Navigate to="/how-we-work" replace /> },
  { path: "how-we-work/embed", element: <Navigate to="/how-we-work" replace /> },
  { path: "how-we-work/overhaul", element: <Navigate to="/how-we-work" replace /> },
  { path: "how-we-work/executive-coaching", element: <Navigate to="/how-we-work" replace /> },

  { path: "blick", element: <Blick /> },
  { path: "careers", element: <Careers /> },
  { path: "about", element: <About /> },
  { path: "privacy", element: <Privacy /> },
  { path: "terms", element: <Terms /> },
  { path: "faq", element: <FAQPage /> },
  { path: "ai-dictionary", element: <AIDictionary /> },
  { path: "animation-preview", element: <AnimationPreview /> },
  { path: "compliance/eu-ai-act", element: <EUAIActOverview /> },
  { path: "compliance/eu-ai-act/deep-dive", element: <EUAIActDeepDive /> },
  { path: "compliance/eu-ai-act/assessment", element: <AIRiskAssessment /> },
  { path: "algorithm-hotel", element: <AlgorithmHotel /> },
  { path: "ai-advantage-lab", element: <AIAdvantageLab /> },
  { path: "ai-advantage-lab/confirmation", element: <AIAdvantageLabConfirmation /> },
  { path: "ai-agents", element: <AIAgents /> },
  { path: "guides/scaling-ai-adoption", element: <GuideScalingAIAdoption /> },

  // ─── Industries — vertical AI Employee landing pages ─────────────────
  { path: "industries", element: <IndustriesHub /> },
  { path: "industries/retail/ai-store-manager", element: <VerticalRoute /> },
  { path: "industries/coffee-chains/ai-operations-manager", element: <VerticalRoute /> },
  { path: "industries/property-management/ai-property-manager", element: <VerticalRoute /> },
  { path: "industries/construction/ai-site-manager", element: <VerticalRoute /> },
  { path: "industries/automotive-service/ai-service-manager", element: <VerticalRoute /> },

  // ─── Solutions — function-level AI Employee landing pages ────────────
  { path: "solutions/customer-service-ai-employees", element: <SolutionRoute /> },
  { path: "solutions/operations-ai-employees", element: <SolutionRoute /> },

  // ─── Swedish SEO pillars ──────────────────────────────────────────────
  // Canonical URL is /sv/ai-agenter; bare /ai-agenter is noindexed by
  // useMetaTags (see english:false in sitemapRoutes.ts).
  { path: "ai-agenter", element: <AIAgenter /> },

  // ─── Legacy redirects ─────────────────────────────────────────────────
  // Old /services/* URLs → new /how-we-work/* URLs. Old Framer aliases
  // retargeted to the new destinations. See docs/redirect-map.md.
  { path: "services", element: <Navigate to="/what-we-do" replace /> },
  { path: "services/ai-adoption-sprint", element: <Navigate to="/how-we-work" replace /> },
  { path: "services/embed", element: <Navigate to="/how-we-work" replace /> },
  { path: "services/overhaul", element: <Navigate to="/how-we-work" replace /> },
  { path: "services/executive-coaching", element: <Navigate to="/how-we-work" replace /> },
  { path: "use-cases", element: <Navigate to="/work" replace /> },
  { path: "ai-adoption-sprint", element: <Navigate to="/how-we-work" replace /> },
  { path: "embed", element: <Navigate to="/how-we-work" replace /> },
  { path: "executive-coaching", element: <Navigate to="/how-we-work" replace /> },
  { path: "executive-ai-coaching", element: <Navigate to="/how-we-work" replace /> },
  { path: "career", element: <Navigate to="/careers" replace /> },
  { path: "contact", element: <Navigate to="/" replace /> },
];

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation} strict>
      <DeferredTooltipProvider>
        <SoundProvider>
        <MotionRailProvider>
        <ScrollRail />
        <MagneticCursor />
        <LoadFilm />
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <BrowserRouter>
            <ScrollToTop />
            <CMSAuthProvider>
              <Routes>
                {/* Localised-slug redirects. `ai-medarbetare` is the SV
                    canonical slug for the AI Employees page; bare EN sends
                    to /ai-employees, and the legacy /sv/ai-employees URL
                    sends to the SV slug. */}
                <Route path="/ai-medarbetare" element={<Navigate to="/ai-employees" replace />} />
                <Route path="/sv/ai-employees" element={<Navigate to="/sv/ai-medarbetare" replace />} />

                {/* English routes — mounted directly, no language wrapper. */}
                {publicRoutes.map((route) => (
                  <Route
                    key={route.path || 'index-default'}
                    index={route.path === ''}
                    path={route.path ? `/${route.path}` : undefined}
                    element={route.element}
                  />
                ))}

                {/* The only two Swedish routes — pages authored in Swedish. */}
                <Route path="/sv/ai-agenter" element={<AIAgenter />} />
                <Route path="/sv/ai-medarbetare" element={<AIEmployees />} />
                {/* Svensk, indexerbar landningssida för åkerier — fristående
                    chrome. Se src/pages/sv/Returfrakt.tsx. */}
                <Route path="/sv/returfrakt" element={<Returfrakt />} />
                {/* Google Ads paid landing page — standalone chrome, noindexed
                    at both robots.txt and page meta. See src/pages/sv/AIArbetsflode.tsx. */}
                <Route path="/ai-arbetsflode" element={<AIArbetsflode />} />
                {/* Kundspecifik konceptkampanj — Tristar Clean. Noindex,
                    fristående chrome, ingen sitemap-post. */}
                <Route path="/tristar" element={<TristarLanding />} />
                <Route path="/tristar/metoden" element={<TristarMetoden />} />
                {/* Link-only BORA service concept. No public navigation or discovery entry. */}
                <Route path="/bora" element={<Bora />} />
                {/* Client onboarding portal — link-only, noindexed, one config per client. */}
                <Route path="/onboarding/:slug" element={<ClientOnboarding />} />
                {/* Any other /sv/* URL falls back to the English homepage. */}
                <Route path="/sv" element={<Navigate to="/" replace />} />
                <Route path="/sv/*" element={<Navigate to="/" replace />} />

                <Route path="/brick-lab" element={<Navigate to="/cms/bricklab" replace />} />
                <Route path="/bricklab" element={<Navigate to="/cms/bricklab" replace />} />

                <Route path="/cms/login" element={<CMSLogin />} />
                <Route
                  path="/cms"
                  element={
                    <ProtectedCMSRoute>
                      <CMSDashboard />
                    </ProtectedCMSRoute>
                  }
                />
                <Route
                  path="/cms/bricklab"
                  element={
                    <ProtectedCMSRoute>
                      <CMSBrickLab />
                    </ProtectedCMSRoute>
                  }
                />
                <Route
                  path="/cms/translations"
                  element={
                    <ProtectedCMSRoute>
                      <CMSTranslations />
                    </ProtectedCMSRoute>
                  }
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
              <ChromeGate>
                <BrickBot />
              </ChromeGate>
            </CMSAuthProvider>
          </BrowserRouter>
        </Suspense>
        </MotionRailProvider>
        </SoundProvider>
      </DeferredTooltipProvider>
    </LazyMotion>
  </QueryClientProvider>
);

export default App;

